<script setup lang="ts">
import { computed, h, onMounted, onUnmounted, ref, watch } from 'vue'
import { useFullScreenDimensions } from '@/composables/helpers/fullscreen'
import { formatNumberFullTruncate } from '@/helpers/utils'
import CoinIcon from '@/assets/images/icons/gold-coin-icon-s3.webp'
import { usePlayer } from '@/composables/game/player'
import { useFortuneWheel } from '@/composables/game/fortune-wheel'
import { useWatchOverlays } from '@/composables/helpers/scoll'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'
import { GameSound, useSounds } from '@/composables/game/sounds'
import CrossCircleIcon from '@/components/icons/CrossCircleIcon.vue'
import { type PuzzlePiece, usePuzzle, usePuzzles, usePuzzlesOverlay } from '@/composables/game/puzzles'
import { formatDuration, formatMillisecondDuration } from '@/helpers/date'
import type { StepReward } from '@/types/shared/pathway-event'
import { useConfetti } from '@/composables/game/confetti'
import api from '@/services/api'
import { useToaster } from '@/composables/game/toasters'
import { allSpecialCurrenciesConfig } from '@/config/special-currencies'
import CoinConfettiImage from '@/assets/images/icons/gold-coin-icon-s3.webp'
import SpinsConfettiImage from '@/assets/images/fortune-wheel.webp'
import StarsConfettiImage from '@/assets/images/premium.png'
import TicketConfettiImage from '@/assets/images/emojis/emoji-ticket.png'
import BaseButton from '@/components/base/BaseButton.vue'
import PuzzleCompletionOverlay from '@/components/overlays/PuzzleCompletionOverlay.vue'
import PuzzlePieceComponent from '@/components/puzzles/PuzzlePiece.vue'
import defaultPuzzleBackground from '@/assets/images/puzzles/puzzle_bg.png'
import RewardLiveOpPurchaseStepModal from '@/components/modals/RewardLiveOpPurchaseStepModal.vue'
import OutOfSpecialCurrencyModal from '@/components/modals/OutOfSpecialCurrencyModal.vue'
import { useOutOfSpecialCurrency, usePathwayEventOverlay, usePathwayEvents } from '@/composables/game/pathway-events'
import ExplodeEmojisAnimation from '@/components/ExplodeEmojisAnimation.vue'
import { useGiveaway } from '@/composables/game/giveaway'
import { useRouter } from 'vue-router'
import { RouteName } from '@/routes'
import DropeeSpinnerIcon from '@/components/icons/DropeeSpinnerIcon.vue'
import { useAnalytics } from '@/composables/analytics'

const props = defineProps<{
	show: boolean
}>()

const emit = defineEmits<{
	(e: 'hide'): void
}>()

const { topNumber } = useFullScreenDimensions()
const { coins, player, goldenCoinValue } = usePlayer()
const { availableSpins, updateState: updateFortuneWheelState } = useFortuneWheel()
const { addHapticFeedback } = useHapticFeedback()
const { playSound } = useSounds()
const { playerTickets } = useGiveaway()
const router = useRouter()
const { trackEvent } = useAnalytics()

const { allPuzzleLiveOps } = usePuzzles()
const { selectedPuzzleId } = usePuzzlesOverlay()

const selectedPuzzleLiveOp = computed(() => {
  if (selectedPuzzleId.value !== null) {
    return allPuzzleLiveOps.value.find(p => p.id === selectedPuzzleId.value) || null
  }
  return allPuzzleLiveOps.value[0] || null
})

const {
  puzzleGrid,
  puzzleBackgroundImage,
  totalRewards,
  countdownForEventEnd,
  completedPuzzles,
  totalPuzzles,
  isPuzzleRewardClaimed,
  playerProgress,
} = usePuzzle(selectedPuzzleLiveOp)

const { updatePlayer } = usePlayer()
const { showConfetti } = useConfetti('confetti-container')

const allPuzzlesCompleted = computed(() => completedPuzzles.value === totalPuzzles.value && totalPuzzles.value > 0)
const showCompletionOverlay = ref(false)
const isClaimingReward = ref(false)
const claimedRewards = ref<Partial<StepReward> | null>(null)
const backgroundVisible = ref(false)

// High-frequency timer for millisecond updates
const currentTimeMs = ref(Date.now())
let animationFrameId: number | null = null

const startHighFrequencyTimer = () => {
  const updateTime = () => {
    currentTimeMs.value = Date.now()
    animationFrameId = requestAnimationFrame(updateTime)
  }
  updateTime()
}

const stopHighFrequencyTimer = () => {
  if (animationFrameId !== null) {
    cancelAnimationFrame(animationFrameId)
    animationFrameId = null
  }
}

const elapsedTime = computed(() => {
  if (!puzzleStartTimestamp.value || !playerProgress.value) return 0

  if (playerProgress.value.endTimestamp) {
    return playerProgress.value.endTimestamp - puzzleStartTimestamp.value
  }
  return currentTimeMs.value - puzzleStartTimestamp.value
})

// Get the start timestamp from when the first puzzle piece was completed
const puzzleStartTimestamp = computed(() => {
  if (!playerProgress.value) {
    return null
  }
  if (playerProgress.value.completedSteps && playerProgress.value.completedSteps.length > 0) {
    // Use userStartTimestamp (when user actually started) for elapsed time tracking
    return playerProgress.value.userStartTimestamp ?? playerProgress.value.startTimestamp
  }
  return null
})

const showCoinBalance = computed(() => {
  if (!selectedPuzzleLiveOp.value) return false
  return Object.values(selectedPuzzleLiveOp.value.config.steps).some(step =>
    !!step.reward.goldenCoins,
  ) || !!selectedPuzzleLiveOp.value.config.reward?.goldenCoins
})

const showSpinBalance = computed(() => {
  if (!selectedPuzzleLiveOp.value) return false
  return Object.values(selectedPuzzleLiveOp.value.config.steps).some(step =>
    !!step.reward.spins,
  ) || !!selectedPuzzleLiveOp.value.config.reward?.spins
})

const showStarBalance = computed(() => {
  if (!selectedPuzzleLiveOp.value) return false
  return Object.values(selectedPuzzleLiveOp.value.config.steps).some(step =>
    !!step.reward.dropeeStars || (step.cost.type === 'star'),
  ) || !!selectedPuzzleLiveOp.value.config.reward?.dropeeStars
})

const showTicketBalance = computed(() => {
  if (!selectedPuzzleLiveOp.value) return false
  return Object.values(selectedPuzzleLiveOp.value.config.steps).some(step =>
    !!step.reward.giveawayTickets,
  ) || !!selectedPuzzleLiveOp.value.config.reward?.giveawayTickets
})

function showSpecialCurrencyBalance(currency: string) {
  if (!selectedPuzzleLiveOp.value) return false
  const hasInStepRewards = Object.values(selectedPuzzleLiveOp.value.config.steps).some(step =>
    !!(step.reward.specialCurrencies as Record<string, number> | undefined)?.[currency] ||
		(step.cost.type === 'specialCurrency' && step.cost.currencyId === currency),
  )

  const hasInChallengeReward = !!(selectedPuzzleLiveOp.value.config.reward?.specialCurrencies as Record<string, number> | undefined)?.[currency]
  return hasInStepRewards || hasInChallengeReward
}

const rewardShapeImageMap = computed<Record<string, string>>(() => ({
  spins: SpinsConfettiImage,
  goldenCoins: CoinConfettiImage,
  giveawayTickets: TicketConfettiImage,
  dropeeStars: StarsConfettiImage,
  ...Object.fromEntries(
    Object.entries(allSpecialCurrenciesConfig.value).map(([key, value]) => [key, value.image]),
  ),
}))

const totalRewardSpecialCurrencies = computed(() => {
  if (!totalRewards.value.specialCurrencies) return []

  return Object.entries(totalRewards.value.specialCurrencies)
    .filter(([, amount]) => amount && amount > 0)
    .map(([currencyId, amount]) => ({
      id: currencyId,
      amount: amount!,
      image: rewardShapeImageMap.value[currencyId],
    }))
})

useWatchOverlays(computed(() => props.show))

// Start/stop timer based on overlay visibility
watch(() => props.show, (isShowing) => {
  if (isShowing && puzzleStartTimestamp.value && !allPuzzlesCompleted.value) {
    startHighFrequencyTimer()
  } else {
    stopHighFrequencyTimer()
  }
}, { immediate: true })

// Also watch for when the puzzle starts
watch(puzzleStartTimestamp, (hasStarted) => {
  if (hasStarted && props.show && !allPuzzlesCompleted.value) {
    startHighFrequencyTimer()
  }
})

// Watch for when all puzzles are completed
watch(allPuzzlesCompleted, (isCompleted) => {
  if (isCompleted) {
    stopHighFrequencyTimer()
  }
})

onMounted(() => {
  setTimeout(() => {
    backgroundVisible.value = true
  }, 300)
})

onUnmounted(() => {
  stopHighFrequencyTimer()
})

const bannerStyle = computed(() => {
  return { background: 'linear-gradient(90deg, #BD2A21 0%, #1C1C1E 100%)' }
})

function getConfettiImages(step: PuzzlePiece['step']) {
  const images: { src: string, width: number, height: number, particles: { size: { value: number } } }[] = []

  Object.keys(step.reward).forEach(rewardType => {
    let imageKey = ''
    const isSpecialCurrency = rewardType === 'specialCurrencies'
    if (isSpecialCurrency) {
      const specialCurrencies = step.reward.specialCurrencies
      if (specialCurrencies) {
        imageKey = Object.keys(specialCurrencies).find(key => (specialCurrencies as Record<string, number>)[key]) ?? ''
      }
    } else {
      imageKey = rewardType
    }
    images.push({
      'src': rewardShapeImageMap.value[imageKey],
      'width': 24,
      'height': 24,
      'particles': {
        'size': {
          'value': 12,
        },
      },
    })
  })

  return images
}

const { notify } = useToaster()
const { getAvailableEcommerceItems, canShowOutOfSpecialCurrencyModal } = useOutOfSpecialCurrency()
const { anyActivePathwayEvent, pathwayEvents } = usePathwayEvents()
const { showFirstPathway } = usePathwayEventOverlay()

// Track which balance should be highlighted with gold circle animation
const goldCircleAnimationItems = ref<Record<string, boolean>>({
  goldenCoins: false,
  spins: false,
  dropeeStars: false,
  giveawayTickets: false,
  ...Object.fromEntries(
    Object.keys(allSpecialCurrenciesConfig.value).map(id => [id, false]),
  ),
})
const loadingPuzzleStep = ref<number | null>(null)
const selectedPuzzlePiece = ref<PuzzlePiece | null>(null)
const showPurchaseModal = ref(false)
const selectedSpecialCurrencyForModal = ref<string | null>(null)
const isLoadingFinalTime = ref(false)

function showGoldenCircles(reward: Partial<PuzzlePiece['step']['reward']>) {
  // Reset all animations
  Object.keys(goldCircleAnimationItems.value).forEach(key => {
    goldCircleAnimationItems.value[key] = false
  })

  // Show animation after a small delay
  setTimeout(() => {
    if (reward.goldenCoins) {
      goldCircleAnimationItems.value.goldenCoins = true
    }
    if (reward.spins) {
      goldCircleAnimationItems.value.spins = true
    }
    if (reward.dropeeStars) {
      goldCircleAnimationItems.value.dropeeStars = true
    }
    if (reward.giveawayTickets) {
      goldCircleAnimationItems.value.giveawayTickets = true
    }
    // Handle special currencies
    if (reward.specialCurrencies) {
      Object.entries(reward.specialCurrencies).forEach(([currency, amount]) => {
        if (amount && amount > 0) {
          goldCircleAnimationItems.value[currency] = true
        }
      })
    }
  }, 100)
}

function onOutOfSpecialCurrency(currencyId: string) {
  if (canShowOutOfSpecialCurrencyModal(currencyId)) {
    selectedSpecialCurrencyForModal.value = currencyId
    return
  }
  notify({
    type: 'error',
    title: `<span class="flex items-center gap-1.5">Not enough <img width="14px" height="14px" style="display: inline-block; vertical-align: middle;" src="${rewardShapeImageMap.value[currencyId]}" alt="Icon"></span>`,
    description: h('span', { class: 'font-medium' }, [
      h('span', {
        class: 'underline text-teal-primary cursor-pointer',
        onClick: () => {
          if (anyActivePathwayEvent.value) {
            showFirstPathway(pathwayEvents.value)
          } else {
            router.push({ name: RouteName.HOME })
          }
          emit('hide')
        },
      }, 'Explore pathways'),
      ' to get more now!',
    ]),
  })
}

function handlePuzzlePieceTap(piece: PuzzlePiece) {
  if (piece.completed || !selectedPuzzleLiveOp.value) {
    return
  }
  trackEvent({
    event: 'puzzlePieceTap',
    metadata: {
      eventId: selectedPuzzleLiveOp.value?.id,
      step: piece.stepNumber,
      timestamp: Date.now(),
    },
  })
}

async function handlePuzzleClick(piece: PuzzlePiece) {
  addHapticFeedback('medium')

  if (piece.completed || !selectedPuzzleLiveOp.value) {
    return
  }

  const cost = piece.step.cost

  // Handle USD purchases - show purchase modal
  if (cost.type === 'star') {
    selectedPuzzlePiece.value = piece
    showPurchaseModal.value = true
    return
  }

  // Handle special currency - check balance
  if (cost.type === 'specialCurrency') {
    const balance = player.value.specialCurrencies[cost.currencyId] || 0
    if (balance < cost.amount) {
      onOutOfSpecialCurrency(cost.currencyId)
      return
    }
  }

  await payForPuzzle(piece)
}

async function payForPuzzle(piece: PuzzlePiece) {
  if (!selectedPuzzleLiveOp.value || !!loadingPuzzleStep.value) return

  loadingPuzzleStep.value = piece.stepNumber

  const isLastPiece = completedPuzzles.value + 1 === totalPuzzles.value
  if (isLastPiece) {
    isLoadingFinalTime.value = true
  }

  try {
    const result = await api.payForRewardLiveOp({
      eventId: selectedPuzzleLiveOp.value.id,
      stepNumber: piece.stepNumber,
    })

    if (!result.success) {
      notify({ type: 'error', title: 'Puzzle claim failed' })
      return
    }

    // Update player state with the returned playerStats
    if (result.playerStats) {
      updatePlayer(result.playerStats)
    }

    await onSuccessfulClaim(piece)
  } catch (e) {
    notify({ type: 'error', title: 'Failed to claim puzzle' })
  } finally {
    loadingPuzzleStep.value = null
    isLoadingFinalTime.value = false
  }
}

async function onSuccessfulClaim(piece: PuzzlePiece) {
  if (selectedPuzzleLiveOp.value) {
    trackEvent({
      event: 'puzzlePieceDone',
      metadata: {
        eventId: selectedPuzzleLiveOp.value.id,
        step: piece.stepNumber,
        timestamp: Date.now(),
        elapsedDuration: elapsedTime.value,
      },
    })
  }
  await updateFortuneWheelState()
  showGoldenCircles(piece.step.reward)
  const rewardDetails = getFirstRewardDetails(piece.step.reward)

  notify({
    type: 'success',
    title: rewardDetails
      ? `<span class="flex items-center gap-1.5">Reward: ${formatNumberFullTruncate(rewardDetails.amount)} <img width="14px" height="14px" style="display: inline-block; vertical-align: middle;" src="${rewardDetails.image}" alt="Reward"></span>`
      : 'Reward claimed!',
  })

  // Show confetti with reward-specific images
  const confettiImages = getConfettiImages(piece.step)
  if (confettiImages.length > 0) {
    showConfetti({
      count: 100,
      decay: 0.9,
      shapes: ['image'],
      shapeOptions: {
        image: confettiImages,
      },
    })
  } else {
    // Default confetti if no specific reward images
    showConfetti({
      count: 100,
      colors: ['#FFD700', '#FFA500', '#FF6347'],
    })
  }
}

function getFirstRewardDetails(reward: Partial<PuzzlePiece['step']['reward']>): {
	image: string,
	amount: number
} | null {
  if (reward.goldenCoins) {
    return { image: rewardShapeImageMap.value.goldenCoins, amount: reward.goldenCoins * goldenCoinValue.value }
  }
  if (reward.spins) {
    return { image: rewardShapeImageMap.value.spins, amount: reward.spins }
  }
  if (reward.dropeeStars) {
    return { image: rewardShapeImageMap.value.dropeeStars, amount: reward.dropeeStars }
  }
  if (reward.giveawayTickets) {
    return { image: rewardShapeImageMap.value.giveawayTickets, amount: reward.giveawayTickets }
  }
  if (reward.specialCurrencies) {
    const firstCurrency = Object.entries(reward.specialCurrencies).find(([, amount]) => amount && amount > 0)
    if (firstCurrency) {
      return {
        image: rewardShapeImageMap.value[firstCurrency[0]],
        amount: firstCurrency[1],
      }
    }
  }
  return null
}

async function handlePurchaseDone() {
  if (!selectedPuzzlePiece.value) return

  const isLastPiece = completedPuzzles.value + 1 === totalPuzzles.value
  if (isLastPiece) {
    isLoadingFinalTime.value = true
  }

  try {
    await onSuccessfulClaim(selectedPuzzlePiece.value)
  } finally {
    isLoadingFinalTime.value = false
  }
}

function closePurchaseModal() {
  showPurchaseModal.value = false
  setTimeout(() => selectedPuzzlePiece.value = null, 100)
}

function closeOutOfSpecialCurrencyModal() {
  selectedSpecialCurrencyForModal.value = null
}

function onCompletionOverlayHide() {
  showCompletionOverlay.value = false

  showGoldenCircles(claimedRewards.value || totalRewards.value)
  claimedRewards.value = null

  emit('hide')
}

async function handleClaim() {
  if (!selectedPuzzleLiveOp.value || !allPuzzlesCompleted.value || isClaimingReward.value) {
    notify({ type: 'error', title: 'Claim error, please try again' })
    return
  }

  addHapticFeedback('medium')
  playSound(GameSound.PATHWAY_COMPLETED)
  isClaimingReward.value = true

  claimedRewards.value = { ...totalRewards.value }

  try {
    const result = await api.claimRewardLiveOp({
      eventId: selectedPuzzleLiveOp.value.id,
    })

    if (!result.success) {
      notify({ type: 'error', title: 'Reward already claimed' })
      return
    }

    if (result.playerStats) {
      updatePlayer(result.playerStats)
    }

    const totalRewardsStep = {
      reward: claimedRewards.value || totalRewards.value,
      cost: { type: 'free' as const },
    } as PuzzlePiece['step']
    const confettiImages = getConfettiImages(totalRewardsStep)
    if (confettiImages.length > 0) {
      showConfetti({
        count: 150,
        spread: 100,
        startVelocity: 45,
        decay: 0.9,
        shapes: ['image'],
        shapeOptions: {
          image: confettiImages,
        },
      })
    } else {
      showConfetti({
        count: 150,
        spread: 100,
        startVelocity: 45,
        colors: ['#FFD700', '#FFA500', '#FF6347', '#9333ea', '#ec4899'],
      })
    }

    // Show completion overlay
    showCompletionOverlay.value = true
  } catch (error) {
    notify({ type: 'error', title: 'Failed to claim reward' })
  } finally {
    isClaimingReward.value = false
  }
}
</script>

<template>
  <Transition
    name="fade"
    mode="out-in"
  >
    <div
      v-if="show"
      v-show="!showCompletionOverlay"
      class="fixed max-h-screen h-screen w-screen overflow-y-auto overflow-x-hidden left-0 bottom-0 bg-dark-most-bottom-layer/[.66] backdrop-blur-[4px] flex flex-col z-[800] px-4 normal-zeros"
      :style="{
        paddingTop: `${topNumber + 24}px`,
      }"
    >
      <div class="h-full w-full max-w-[440px] mx-auto flex-1 flex flex-col pb-4">
        <div class="flex items-start z-[1] gap-x-4">
          <div class="flex-1 flex items-center gap-1 flex-wrap">
            <template
              v-for="specialCurrency in Object.keys(allSpecialCurrenciesConfig)"
              :key="specialCurrency"
            >
              <div
                v-if="showSpecialCurrencyBalance(specialCurrency)"
                class="balance-pill scale-in"
              >
                <div class="w-5 h-5 min-w-[20px] relative flex justify-center">
                  <img
                    class="object-cover w-auto h-5"
                    :src="rewardShapeImageMap[specialCurrency]"
                    alt="Special Currency"
                  >
                  <ExplodeEmojisAnimation
                    :show="goldCircleAnimationItems[specialCurrency]"
                    :image-src="undefined"
                    :loop="2"
                  />
                </div>
                <span>{{ formatNumberFullTruncate((player.specialCurrencies as Record<string, number>)[specialCurrency] ?? 0) }}</span>
              </div>
            </template>
            <div
              v-if="showCoinBalance"
              class="balance-pill scale-in"
            >
              <div class="w-5 h-5 min-w-[20px] relative">
                <img
                  :src="CoinIcon"
                  class="w-full h-full"
                >
                <ExplodeEmojisAnimation
                  :show="goldCircleAnimationItems.goldenCoins"
                  :image-src="undefined"
                  :loop="2"
                />
              </div>
              <span>{{ formatNumberFullTruncate(coins) }}</span>
            </div>
            <div
              v-if="showSpinBalance"
              class="balance-pill scale-in"
            >
              <div class="w-5 h-5 min-w-[20px] relative">
                <img
                  class="object-cover w-full h-full"
                  src="@/assets/images/fortune-wheel.webp"
                  alt="Spins"
                >
                <ExplodeEmojisAnimation
                  :show="goldCircleAnimationItems.spins"
                  :image-src="undefined"
                  :loop="2"
                />
              </div>
              <span>{{ formatNumberFullTruncate(availableSpins) }}</span>
            </div>
            <div
              v-if="showStarBalance"
              class="balance-pill scale-in"
            >
              <div class="w-5 h-5 min-w-[20px] relative">
                <img
                  class="object-cover size-5 min-w-[20px]"
                  src="@/assets/images/premium.png"
                  alt="Stars"
                >
                <ExplodeEmojisAnimation
                  :show="goldCircleAnimationItems.dropeeStars"
                  :image-src="undefined"
                  :loop="2"
                />
              </div>
              <span>{{ formatNumberFullTruncate(player.ecommerce.starsBalance) }}</span>
            </div>
            <div
              v-if="showTicketBalance"
              class="balance-pill scale-in"
            >
              <div class="w-5 h-5 min-w-[20px] relative">
                <img
                  class="w-5 h-5 min-w-[20px] object-cover"
                  src="@/assets/images/emojis/emoji-ticket.png"
                  alt="Tickets"
                >
                <ExplodeEmojisAnimation
                  :show="goldCircleAnimationItems.giveawayTickets"
                  :image-src="undefined"
                  :loop="2"
                />
              </div>
              <span>{{ formatNumberFullTruncate(playerTickets) }}</span>
            </div>
          </div>
          <CrossCircleIcon
            class="size-8 min-w-[32px] text-white/[87%]"
            @click="addHapticFeedback('light'); emit('hide')"
          />
        </div>

        <div class="w-full h-full flex flex-col">
          <div class="pt-4 w-full flex-1 flex flex-col items-center">
            <!-- Rewards Box -->
            <div class="z-10 mt-4 mx-2.5 w-full rewards-box relative rounded-xl p-0.5 max-w-[339px]">
              <div class="bg-dark-top-layer/[.44] backdrop-blur rounded-xl pb-4 pt-6 px-8">
                <div
                  class="absolute -top-[18px] left-1/2 -translate-x-1/2 text-center text-white black-stroke text-2xl font-semibold w-full"
                >
                  Reveal the image to win
                </div>
                <div class="flex items-center justify-center gap-4 flex-wrap">
                  <!-- Golden Coins -->
                  <div
                    v-if="totalRewards.goldenCoins && totalRewards.goldenCoins > 0"
                    class="flex flex-col items-center"
                  >
                    <img
                      :src="CoinIcon"
                      class="w-auto h-9 -mb-3"
                    >
                    <span
                      class="text-white text-2xl font-bold gray-text-stroke"
                    >{{ formatNumberFullTruncate(totalRewards.goldenCoins * goldenCoinValue)
                    }}</span>
                  </div>
                  <!-- Special Currencies -->
                  <div
                    v-for="currency in totalRewardSpecialCurrencies"
                    :key="currency.id"
                    class="flex flex-col items-center"
                  >
                    <img
                      :src="currency.image"
                      class="w-auto h-9 -mb-3 object-contain"
                      :alt="currency.id"
                    >
                    <span
                      class="text-white text-2xl font-bold gray-text-stroke"
                    >{{ formatNumberFullTruncate(currency.amount)
                    }}</span>
                  </div>
                  <!-- Spins -->
                  <div
                    v-if="totalRewards.spins && totalRewards.spins > 0"
                    class="flex flex-col items-center"
                  >
                    <img
                      src="@/assets/images/fortune-wheel.webp"
                      class="w-auto h-9 -mb-3 object-contain"
                      alt="Spins"
                    >
                    <span
                      class="text-white text-2xl font-bold gray-text-stroke"
                    >{{ formatNumberFullTruncate(totalRewards.spins)
                    }}</span>
                  </div>
                  <!-- Dropee Stars -->
                  <div
                    v-if="totalRewards.dropeeStars && totalRewards.dropeeStars > 0"
                    class="flex flex-col items-center"
                  >
                    <img
                      src="@/assets/images/premium.png"
                      class="w-auto h-9 -mb-3 object-contain"
                      alt="Dropee Stars"
                    >
                    <span
                      class="text-white text-2xl font-bold gray-text-stroke"
                    >{{ formatNumberFullTruncate(totalRewards.dropeeStars)
                    }}</span>
                  </div>
                  <!-- Tickets -->
                  <div
                    v-if="totalRewards.giveawayTickets && totalRewards.giveawayTickets > 0"
                    class="flex flex-col items-center"
                  >
                    <img
                      src="@/assets/images/emojis/emoji-ticket.png"
                      class="w-auto h-9 -mb-3 object-contain"
                      alt="Tickets"
                    >
                    <span
                      class="text-white text-2xl font-bold gray-text-stroke"
                    >{{ formatNumberFullTruncate(totalRewards.giveawayTickets)
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
            <!-- Banner -->
            <div
              class="-mt-3 h-14 -mx-8 z-[1] shadow-banner flex items-center justify-center w-[calc(100%+64px)]"
              :style="bannerStyle"
            >
              <span class="mr-3 black-stroke uppercase font-bold text-[20px] leading-6 banner-ends-in">Ends in</span>
              <span
                class="golden-stroke-black-text font-bold text-[20px] leading-6 uppercase shadow-countdown px-px min-w-[95px]"
              >
                {{ formatDuration(countdownForEventEnd) }}
              </span>
            </div>

            <!-- Puzzle Grid -->
            <div class="mt-3.5 mb-2.5 w-full puzzle-container overflow-visible flex justify-center">
              <div
                class="relative mx-auto max-w-[339px] w-full h-[357px] rounded-xl drop-shadow-[0_0_12px_rgba(0,0,0,0.44)]"
              >
                <!-- Background image that gets revealed -->
                <div class="absolute inset-0 overflow-hidden rounded-xl">
                  <div
                    v-if="!backgroundVisible"
                    class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 origin-center"
                  >
                    <DropeeSpinnerIcon
                      class="w-12 h-12 min-w-[48px] opacity-70"
                    />
                  </div>
                  <img
                    :src="puzzleBackgroundImage || defaultPuzzleBackground"
                    class="w-full h-full object-cover transition-opacity duration-300"
                    :class="{ 'opacity-0': !backgroundVisible }"
                    alt="Puzzle Background"
                  >
                </div>

                <!-- Puzzle grid -->
                <div class="relative w-full h-full">
                  <PuzzlePieceComponent
                    v-for="(piece, index) in puzzleGrid"
                    :key="`${piece.stepKey}_${index}`"
                    :piece="piece"
                    :index="index"
                    :class="{ 'opacity-80': loadingPuzzleStep === piece.stepNumber, 'pointer-events-none': !!loadingPuzzleStep || piece.completed }"
                    v-touch:tap="() => handlePuzzlePieceTap(piece)"
                    @click="handlePuzzleClick(piece)"
                  />
                </div>

                <!-- Timer Display -->
                <div class="flex justify-end -mt-3.5 pt-3.5 pb-0.5 px-2 overflow-hidden rounded-b-xl relative -z-[1]">
                  <div class="blur-md absolute inset-0 bg-dark-top-layer/[.44] -z-[1] " />
                  <div class="text-white text-xs leading-5 font-bold flex items-center relative">
                    <DropeeSpinnerIcon
                      class="absolute w-3 h-3 min-w-[12px] m-[2px] left-1/2 -ml-1.5"
                      :class="{ 'opacity-0': !isLoadingFinalTime}"
                    />
                    <span :class="{ 'opacity-0': isLoadingFinalTime}">
                      {{ formatMillisecondDuration(elapsedTime) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <!--						Claim-->
            <div class="flex justify-center pb-4 mt-[22px]">
              <BaseButton
                style-type="primary-pink"
                spinner-class="w-4 h-4 min-w-[16px]"
                class="min-w-[200px]"
                :disabled="!allPuzzlesCompleted || isPuzzleRewardClaimed || isClaimingReward"
                :loading="isClaimingReward"
                @click="handleClaim"
              >
                Claim
              </BaseButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>

  <!-- Puzzle Completion Overlay -->
  <PuzzleCompletionOverlay
    :show="showCompletionOverlay"
    :total-rewards="claimedRewards || totalRewards"
    @hide="onCompletionOverlayHide"
  />

  <!-- Purchase Modal for USD payments -->
  <RewardLiveOpPurchaseStepModal
    v-if="selectedPuzzlePiece && showPurchaseModal && selectedPuzzleLiveOp"
    :show="showPurchaseModal"
    :piece="selectedPuzzlePiece"
    :live-op="selectedPuzzleLiveOp"
    @purchase-done="handlePurchaseDone"
    @hide="closePurchaseModal"
  />

  <!-- Out of Special Currency Modal -->
  <OutOfSpecialCurrencyModal
    v-if="selectedSpecialCurrencyForModal"
    :show="!!selectedSpecialCurrencyForModal"
    :currency="selectedSpecialCurrencyForModal"
    :ecommerce-items="getAvailableEcommerceItems(selectedSpecialCurrencyForModal)"
    @hide="closeOutOfSpecialCurrencyModal"
  />
</template>

<style scoped>

.rewards-box::before {
	content: '';
	position: absolute;
	inset: 0;
	padding: 2px; /* Border width */
	background: linear-gradient(90deg, rgba(255, 255, 255, 0.4) 0%, #FFF 50.11%, rgba(255, 255, 255, 0.4) 100%);
	-webkit-mask: linear-gradient(#fff 0 0) content-box,
	linear-gradient(#fff 0 0);
	-webkit-mask-composite: xor;
	mask-composite: exclude;
	pointer-events: none;
	z-index: 0;
	border-radius: 12px; /* Match the parent radius */
}

/*
.rewards-box {
	background: linear-gradient(90deg, rgba(255, 255, 255, 0.40) 0%, #FFF 50.11%, rgba(255, 255, 255, 0.40) 100%);
}*/
.balance-pill {
	@apply py-1.5 pl-2.5 pr-3 rounded-full bg-dark-top-most-layer flex items-center gap-x-1.5 h-8 text-sm font-semibold -tracking-[0.28px] min-w-max;
	position: relative;
	transition: all 0.3s ease;
}

.scale-in {
	animation: scaleIn 0.3s ease-out;
}

@keyframes scaleIn {
	from {
		transform: scale(0.9);
		opacity: 0;
	}
	to {
		transform: scale(1);
		opacity: 1;
	}
}

.shadow-banner {
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
}

.black-stroke {
	-webkit-text-stroke-width: 2px;
	-webkit-text-stroke-color: #000;
	paint-order: stroke fill;
}

.gray-text-stroke {
	-webkit-text-stroke-width: 5px;
	-webkit-text-stroke-color: #3A3A3C;
	text-shadow: 0 1.457px 2.915px rgba(0, 0, 0, 0.32);
	paint-order: stroke fill;
}

.banner-ends-in {
	text-shadow: 0px 0px 115.874px rgba(0, 0, 0, 0.50);
}

.shadow-countdown {
	filter: drop-shadow(0px 0px 12px #000);
}

.normal-zeros {
	padding-bottom: env(safe-area-inset-bottom);
}
</style>